"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { HmcDropdownOption } from "@adi-ctx/harmonic-core-components-react";
import { type TDropdownMenuItem } from "@/components";
import "./dropdown-menu.scss";
import { usePathname } from "next/navigation";

type DropdownMenuProps = {
	readonly items: TDropdownMenuItem[];
	readonly isOpen: boolean;
	readonly orientation?: "left" | "right";
	readonly selected?: TDropdownMenuItem;
	readonly onClose?: () => void;
	readonly onSelect: (item: TDropdownMenuItem) => void;
};

/**
 * Renders a dropdown menu and calls "onSelect"
 *
 * Using with the withDropdownMenu HOC:
 * const WithDropdownMenu = withDropdownMenu(BaseComponent);
 * <WithDropdownMenu menuItems=[] selected={} onSelect={}, onClose={} />
 *
 * @constructor
 */
function DropdownMenu({
	isOpen: defaultIsOpen,
	items,
	selected,
	orientation,
	onClose,
	onSelect,
}: DropdownMenuProps) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const pathName = usePathname();
	const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen);

	// Used when path is provided for the menu items
	// adding manually the active state to the active option
	useEffect(() => {
		const $options = document.querySelectorAll("hmc-dropdown-option") || [];
		$options.forEach(($option: HTMLElement, index: number) => {
			if (items?.[index].path && pathName.includes(items[index].path!)) {
				$option.shadowRoot?.append(
					Object.assign(document.createElement("style"), {
						innerText:
							".option { background-color: #edf3fc!important; color: #0067b9!important }",
					})
				);
			}
		});
	}, [items, pathName]);

	const handleKeyDown = useCallback(
		(e: any) => {
			if (e.key !== "Escape") return;

			const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
			if (!isNotCombinedKey) return;

			setIsOpen(false);

			if (typeof onClose === "function") {
				onClose();
			}
		},
		[onClose]
	);

	const handleClick = useCallback(
		(e: MouseEvent) => {
			// Check if clicked outside the DropdownMenu
			const isOutside = Boolean(
				!containerRef.current?.contains(e.target as Node)
			);
			setIsOpen(!isOutside);

			if (isOutside) {
				e.preventDefault();

				if (typeof onClose === "function") {
					onClose();
				}
			}
		},
		[onClose]
	);

	// Click
	useEffect(() => {
		document.addEventListener("click", handleClick, true);

		return () => {
			document.removeEventListener("click", handleClick, true);
		};
	}, [handleClick]);

	// ESC key
	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleKeyDown]);

	// isOpen can change from outside
	useEffect(() => {
		setIsOpen(defaultIsOpen);
	}, [defaultIsOpen]);

	return (
		<div
			ref={containerRef}
			className={`flex flex-col ${orientation === "left" ? "dropdown-menu" : "dropdown-menu-user"}`}
			aria-expanded={isOpen}
			role="listbox"
			data-testid="dropdown-menu"
			data-teststatus={isOpen ? "open" : "closed"}
		>
			{items?.map((item: TDropdownMenuItem) => (
				<HmcDropdownOption
					key={item.id}
					value={item}
					isSelected={
						item.id === selected?.id /* used when no path is provided */
					}
					onHmcDropdownSelection={({ detail: { value: item } }) => {
						onSelect(item as TDropdownMenuItem);
					}}
				>
					<span className="items-left flex gap-2">
						{item.icon} {item.label}
					</span>
				</HmcDropdownOption>
			))}
		</div>
	);
}

export default DropdownMenu;
